import { useEffect } from 'react';
import { Cookie, Shield, Settings, Eye, Database, Globe } from 'lucide-react';

const Cookies = () => {
  useEffect(() => {
    document.title = 'Cookie Policy - FemSportNet';
  }, []);

  const cookieTypes = [
    {
      icon: <Shield className="h-6 w-6 text-green-500" />,
      title: 'Essential Cookies',
      description: 'Required for basic website functionality and security',
      examples: ['Authentication tokens', 'Security preferences', 'Session management'],
      canDisable: false
    },
    {
      icon: <Settings className="h-6 w-6 text-blue-500" />,
      title: 'Functional Cookies',
      description: 'Remember your preferences and settings',
      examples: ['Language preferences', 'Theme settings', 'Layout preferences'],
      canDisable: true
    },
    {
      icon: <Eye className="h-6 w-6 text-purple-500" />,
      title: 'Analytics Cookies',
      description: 'Help us understand how you use our platform',
      examples: ['Page views', 'Feature usage', 'Performance metrics'],
      canDisable: true
    },
    {
      icon: <Globe className="h-6 w-6 text-orange-500" />,
      title: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements',
      examples: ['Ad targeting', 'Social media integration', 'Campaign tracking'],
      canDisable: true
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Learn about how we use cookies and similar technologies to improve your experience on FemSportNet.
            </p>
            <p className="text-green-100">
              Last updated: March 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* What Are Cookies */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Cookie className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                What Are Cookies?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Cookies are small text files that are stored on your device when you visit our website. 
                They help us provide you with a better, more personalized experience.
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                How We Use Cookies
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                <li>Remember your login status and preferences</li>
                <li>Analyze how you use our platform to improve our services</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure the security and proper functioning of our platform</li>
                <li>Measure the effectiveness of our marketing campaigns</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Types of Cookies We Use
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {cookieTypes.map((type, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-600 rounded-lg mr-3">
                      {type.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {type.title}
                      </h3>
                      <div className="flex items-center mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          type.canDisable 
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {type.canDisable ? 'Optional' : 'Required'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {type.description}
                  </p>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Examples:</h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      {type.examples.map((example, exampleIndex) => (
                        <li key={exampleIndex}>{example}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Management */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Managing Your Cookie Preferences
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Browser Settings
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  You can control cookies through your browser settings:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                  <li>Block all cookies</li>
                  <li>Block third-party cookies only</li>
                  <li>Delete existing cookies</li>
                  <li>Set cookie preferences for specific websites</li>
                </ul>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Platform Settings
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Manage your cookie preferences directly on FemSportNet:
                </p>
                <div className="space-y-3">
                  <label className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Analytics Cookies</span>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Marketing Cookies</span>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Functional Cookies</span>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                  </label>
                </div>
                <button className="mt-4 w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors">
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Third-Party Services
            </h2>
            
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-8">
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We use trusted third-party services that may set their own cookies:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Analytics</h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Google Analytics - Website usage analytics</li>
                    <li>• Mixpanel - User behavior tracking</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Social Media</h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Facebook - Social login and sharing</li>
                    <li>• Twitter - Social sharing features</li>
                    <li>• Instagram - Content integration</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Payment Processing</h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Stripe - Secure payment processing</li>
                    <li>• PayPal - Alternative payment method</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Support</h3>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Intercom - Customer support chat</li>
                    <li>• Zendesk - Help desk functionality</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Questions About Cookies?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              If you have any questions about our use of cookies, please don't hesitate to contact us.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Email</h4>
                  <p className="text-gray-600 dark:text-gray-300">privacy@femsportnet.com</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Phone</h4>
                  <p className="text-gray-600 dark:text-gray-300">+1 (555) 123-4567</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Address</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    123 Tech Street, Suite 400<br />
                    San Francisco, CA 94105
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Response Time</h4>
                  <p className="text-gray-600 dark:text-gray-300">Within 2 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Policy Updates
            </h2>
            
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Changes to This Policy
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. We will notify you of any 
                material changes by:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 mb-6">
                <li>Posting the updated policy on this page</li>
                <li>Updating the "Last updated" date at the top of this policy</li>
                <li>Sending you an email notification if you have an account with us</li>
                <li>Displaying a prominent notice on our platform</li>
              </ul>
              
              <p className="text-gray-600 dark:text-gray-300">
                We encourage you to review this Cookie Policy periodically to stay informed about 
                how we use cookies and similar technologies.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cookies;